import React, { FC, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { PoolSizeAdjustmentInput } from 'shared';
import { v4 as uuidv4 } from 'uuid';

import { AlertDialogWrapper } from '../../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { CloseModalButton } from '../../../../components/close-modal-button/index';
import { Sheet, SheetContent } from '../../../../components/sheet/index';
import { AnalyticHooks, Pool, useModalState, useReactForm } from '../../../../hooks/index';
import { PossibleAction } from '../../../../types/google-analytics-types.types';
import { DilutionCondition } from '../../../../types/pool-plans.types';
import { getS3FileOriginalName } from '../../../../utils/get-s3file-original-name.util';
import { handleFormInvalid } from '../../../../utils/handle-form-invalid.util';
import { toBackendDateFormat } from '../../../../utils/to-backend-date-format.util';
import { toNumber } from '../../../../utils/to-number.util';
import {
  mergeFileLinksWithDocusignForSave,
  resolveManualFileLinksExcludingDocusign,
} from '../../../events/event-form/utils/resolve-linked-signature-file-links.util';
import { adjustPoolSizeStore } from '../../adjust-pool-size/adjust-pool-size.store';
import { UpdateConfirmationModal } from '../../update-confirmation-modal';
import { ChildrenFormProps, EditShareClassStepVariant } from '../pool-form.types';
import { FormSchema, formSchema } from '../pool-form.validation';
import { EditPreview, StepOne, StepTwo } from '../steps/index';
import { resolveStagedPoolSizeAdjustments } from './staged-pool-size-adjustments.util';

type ModalProps = {
  companyId: string;
  docusignFileLinks?: string[];
  handleClose: () => void;
  handleTerminate: (state: { date: Date; id: string }) => void;
  invalidateQuery: () => void;
  isOpenModal: boolean;
  onEditClick: (props: { id: string; step: EditShareClassStepVariant }) => void;
  poolId: string;
  previewMode?: true;
  step: EditShareClassStepVariant;
};

const formSteps: ((
  props: {
    handleTerminate: (date: Date) => void;
    onEditClick: (step: EditShareClassStepVariant) => void;
  } & ChildrenFormProps,
) => ReactElement)[] = [
  (props) => <StepOne {...props} />,
  (props) => <StepTwo {...props} />,
  (props) => <EditPreview {...props} />,
];

export const EditPoolModal: FC<ModalProps> = ({
  companyId,
  isOpenModal,
  handleClose,
  invalidateQuery,
  onEditClick,
  poolId,
  step,
  previewMode,
  docusignFileLinks,
  handleTerminate,
}) => {
  const { pool } = Pool.usePool({ companyId, poolId });
  const effectiveDocusignFileLinks = docusignFileLinks ?? pool?.docusignFileLinks;
  const openAdjustPoolSize = adjustPoolSizeStore(({ open }) => open);
  // Adjust Pool Size confirmed from this blade waits for the blade's save; a cancel drops it. Each
  // one starts from the size the ones before leave, in the order they were confirmed.
  const [stagedSizeAdjustments, setStagedSizeAdjustments] = useState<PoolSizeAdjustmentInput[]>([]);

  const {
    control,
    reset,
    setValue,
    getValues,
    getFieldState,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isDirty },
  } = useReactForm({
    schema: formSchema,
  });

  useEffect(() => {
    // A refetched pool must not wipe the edits in progress, a staged size adjustment included.
    if (!pool || isDirty) return;

    const files: {
      abort: () => void;
      doc: {
        name: string;
        size: number;
        type: string;
      };
      docLink: string;
      id: string;
      loadProgress: 100;
    }[] = resolveManualFileLinksExcludingDocusign(
      pool?.filesLinks ?? [],
      effectiveDocusignFileLinks ?? [],
    ).map((link) => {
      const originalFileName = getS3FileOriginalName(link);

      return {
        abort: () => '',
        loadProgress: 100,
        doc: {
          name: originalFileName || '',
          size: 10,
          type: 'application/pdf',
        },
        docLink: link,
        id: uuidv4(),
      };
    });

    reset({
      stepOne: {
        date: new Date(pool?.date),
        // A number, as the live mirror below writes it: a string here would keep the form dirty
        // after an edit is undone, and the blade would stop re-syncing from the pool.
        dillutedSharesCount: toNumber(pool?.dillutedSharesCount),
        dilutionCondition: pool?.capTableInclusion
          ? DilutionCondition.INCLUDE_CAP_TABLE
          : DilutionCondition.EXCLUDE_CAP_TABLE,
        name: pool?.name,
        shareClass: {
          id: pool?.shareClass?.id,
          name: pool?.shareClass?.name,
        },
      },
      stepTwo: {
        additionalNotes: pool?.additionalNotes || undefined,
        files: files,
      },
    });
  }, [effectiveDocusignFileLinks, isDirty, pool, reset]);

  const stagedSize = useMemo(
    () =>
      pool
        ? resolveStagedPoolSizeAdjustments({
            adjustments: stagedSizeAdjustments,
            currentPoolSize: toNumber(pool.dillutedSharesCount),
            grantedShares: toNumber(pool.granted),
          })
        : null,
    [pool, stagedSizeAdjustments],
  );

  // The size is read-only in this blade: it mirrors the live size, or the size the staged
  // adjustments set on save, without touching the other fields. A staged size is an edit, so it
  // dirties the form like one.
  const shownPoolSize = stagedSize?.newPoolSize;
  const isSizeStaged = !!stagedSize?.requests.length;
  useEffect(() => {
    if (shownPoolSize === undefined) return;
    setValue('stepOne.dillutedSharesCount', shownPoolSize, { shouldDirty: isSizeStaged });
  }, [isSizeStaged, setValue, shownPoolSize]);

  const companyEditPoolTracker = AnalyticHooks.useClick(PossibleAction.EDIT_POOL);
  const { update } = Pool.useUpdate();
  const { adjustSize } = Pool.useAdjustSize({ showSuccessToast: false });

  const handleCloseModal = useCallback(() => {
    handleClose();
    invalidateQuery();
    reset();
    setStagedSizeAdjustments([]);
  }, [handleClose, invalidateQuery, reset]);

  const handleUpdateData = useCallback(
    (data: FormSchema) => {
      const { stepOne, stepTwo } = data;
      const updatePool = () =>
        update(
          {
            poolId,
            companyId,
            data: {
              capTableInclusion: DilutionCondition.INCLUDE_CAP_TABLE === stepOne.dilutionCondition,
              date: toBackendDateFormat(stepOne.date),
              name: stepOne.name,
              additionalNotes: stepTwo?.additionalNotes,
              filesLinks: mergeFileLinksWithDocusignForSave(
                stepTwo?.files?.reduce<string[]>((prev, curr) => [...prev, curr.docLink], []) || [],
                effectiveDocusignFileLinks ?? [],
              ),
              shareClassId: stepOne.shareClass.id,
            },
          },
          {
            onSuccess: () => {
              companyEditPoolTracker();
              toast.success(<AppFormattedMessage id="POOL_SUCCESSFULLY_UPDATED" />);
              handleCloseModal();
              invalidateQuery();
              handleClose();
            },
          },
        );

      // The size goes first and the rest only once it is through, so an adjustment the API
      // refuses (shares granted since it was staged, say) stops the save there. Each request is
      // chained on settle, not success: the mutation ignores a new call until the last one settles.
      const applySize = ([data, ...rest]: PoolSizeAdjustmentInput[]) => {
        if (!data) {
          updatePool();
          return;
        }

        adjustSize(
          { companyId, poolId, data },
          {
            onSettled: (_result, error) => {
              if (!error) applySize(rest);
            },
          },
        );
      };

      applySize(stagedSize?.requests ?? []);
    },
    [
      adjustSize,
      companyEditPoolTracker,
      companyId,
      handleClose,
      handleCloseModal,
      invalidateQuery,
      poolId,
      stagedSize,
      update,
      effectiveDocusignFileLinks,
    ],
  );

  const {
    toggler: confirmationModalToggler,
    isOpen: isOpenConfirmationModal,
    handleOpenModal: handleOpenConfirmationModal,
    handleCloseModal: handleCloseConfirmationModal,
    handleSuccessModal: handleSuccessConfirmationModal,
  } = useModalState({
    onSuccess: () => {
      handleSubmit(handleUpdateData, handleFormInvalid)();
      handleCloseModal();
    },
  });

  return (
    <Sheet onOpenChange={(open) => !open && handleCloseModal()} open={isOpenModal}>
      <SheetContent
        className="w-full max-w-[485px] border-transparent bg-transparent p-2 shadow-none"
        onInteractOutside={(event) => {
          // The Adjust Pool Size modal opened from this blade renders outside it; moving focus
          // into it must not close the blade underneath.
          if (adjustPoolSizeStore.getState().isOpen) {
            event.preventDefault();
            return;
          }
          handleCloseModal();
        }}
        side="RIGHT"
      >
        <AlertDialogWrapper
          control={{ onOpenChange: confirmationModalToggler, open: isOpenConfirmationModal }}
        >
          <UpdateConfirmationModal
            onClose={handleCloseConfirmationModal}
            onSuccess={handleSuccessConfirmationModal}
            text="pool"
          />
        </AlertDialogWrapper>
        <div className="flex h-full w-full flex-col overflow-hidden rounded-lg border-[1px] border-gray-300 bg-gray-100 pb-4">
          <div className="flex h-fit w-full items-center justify-between bg-white px-6 py-3">
            <span className="truncate text-xl font-[550] text-gray-700">
              {previewMode ? (
                pool?.name
              ) : (
                <AppFormattedMessage
                  id="EDIT_POOL"
                  values={{
                    text: 'Pool',
                  }}
                />
              )}
            </span>
            <CloseModalButton onClose={handleCloseModal} />
          </div>
          <div className="flex h-full flex-col gap-4 overflow-hidden">
            {formSteps[step - 1]({
              companyId,
              docusignFileLinks: effectiveDocusignFileLinks,
              setError,
              formData: getValues,
              nextFormStep: handleOpenConfirmationModal,
              prevFormStep: () => {},
              setFormData: setValue,
              control,
              handleCloseModal,
              filedState: getFieldState,
              errors,
              clearErrors,
              lockMode: true,
              previewMode,
              onAdjustPoolSizeClick: previewMode
                ? undefined
                : () =>
                    openAdjustPoolSize({
                      onStage: (input) => setStagedSizeAdjustments((staged) => [...staged, input]),
                      poolId,
                      stagedPoolSize: isSizeStaged ? shownPoolSize : undefined,
                    }),
              handleTerminate: (date) => handleTerminate({ date, id: poolId }),
              isTerminated: pool?.isTerminated,
              terminationDate: pool?.terminationDate,
              onEditClick: (step) => onEditClick({ id: poolId, step }),
            })}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
